import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import axios from 'axios';
import { C } from '../theme';
import { countries, getCitiesByCountry, City } from '../data/locations';
import LocationPicker from '../components/LocationPicker';
import { useAuth } from '../contexts/AuthContext';

const landTypes = [
  'Poljoprivredno zemljište',
  'Gradsko zemljište',
  'Šumsko zemljište',
  'Zemljište za izgradnju',
  'Zemljište za rekreaciju',
];

interface AdImage {
  id: number;
  filename: string;
  thumbnail_filename: string;
  order_index: number;
}

interface Ad {
  id: number;
  title: string;
  description: string;
  price: number;
  country_id: string;
  city_id: string;
  land_type: string;
  area: number;
  images: AdImage[];
}

const landTypeLabelMap: Record<string, string> = {
  agricultural: 'Poljoprivredno zemljište',
  residential: 'Gradsko zemljište',
  commercial: 'Šumsko zemljište',
  industrial: 'Zemljište za izgradnju',
  recreational: 'Zemljište za rekreaciju',
};

const landTypeValueMap: Record<string, string> = {
  'Poljoprivredno zemljište': 'agricultural',
  'Gradsko zemljište': 'residential',
  'Šumsko zemljište': 'commercial',
  'Zemljište za izgradnju': 'industrial',
  'Zemljište za rekreaciju': 'recreational',
};

const EditAd: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [ad, setAd] = useState<Ad | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');
  const [landType, setLandType] = useState('');
  const [area, setArea] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [existingImages, setExistingImages] = useState<AdImage[]>([]);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // Redirect non-authenticated users to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/ads/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const adData = response.data;
        setAd(adData);
        setTitle(adData.title);
        setDescription(adData.description);
        setPrice(adData.price.toString());
        setCountryId(adData.country_id);
        setCityId(adData.city_id);
        setLandType(landTypeLabelMap[adData.land_type] || adData.land_type);
        setArea(adData.area.toString());
        setExistingImages(adData.images);
        if (adData.latitude != null && adData.longitude != null) {
          setLocation({ lat: Number(adData.latitude), lng: Number(adData.longitude) });
        }
      } catch (err) {
        setError('Greška pri učitavanju oglasa');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchAd();
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (countryId) {
      setAvailableCities(getCitiesByCountry(Number(countryId)));
    } else {
      setAvailableCities([]);
    }
  }, [countryId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      if (existingImages.length + images.length + newFiles.length > 10) {
        setError('Možete dodati maksimalno 10 slika');
        return;
      }

      setImages(prev => [...prev, ...newFiles]);
      
      // Create preview URLs
      const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  };

  const removeExistingImage = async (imageId: number) => {
    try {
      const token = localStorage.getItem('token');
              await axios.delete(`/api/ads/${id}/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } catch (err) {
      setError('Greška pri brisanju slike');
    }
  };

  const removeNewImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      const newUrls = [...prev];
      URL.revokeObjectURL(newUrls[index]);
      return newUrls.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('price', price);
      formData.append('country_id', countryId);
      formData.append('city_id', cityId);
      formData.append('land_type', landTypeValueMap[landType] || landType);
      formData.append('area', area);
      if (location) {
        formData.append('latitude', String(location.lat));
        formData.append('longitude', String(location.lng));
      }
      
      images.forEach(image => {
        formData.append('images', image);
      });

      const token = localStorage.getItem('token');
      await axios.put(
        `/api/ads/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      navigate('/my-ads');
    } catch (err) {
      setError('Greška pri ažuriranju oglasa');
      setSubmitting(false); // keep spinning on success until the redirect
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!ad) {
    return (
      <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` }}>
            <Typography color="error">Oglas nije pronađen</Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 600,
              mb: 2,
              color: C.white,
            }}
          >
            Izmeni oglas
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: C.muted,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Ažurirajte informacije o vašem zemljištu i poboljšajte vidljivost oglasa
          </Typography>
        </Box>

        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 },
            backgroundColor: C.surface,
            borderRadius: 4,
            border: `1px solid ${C.line}`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Osnovne informacije
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Naslov oglasa"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Npr. Atraktivno zemljište u Beogradu"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Cena (€)"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon sx={{ color: C.muted }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Površina (m²)"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  type="number"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Lokacija
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Država</InputLabel>
                  <Select
                    value={countryId}
                    label="Država"
                    onChange={(e) => setCountryId(e.target.value)}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Grad</InputLabel>
                  <Select
                    value={cityId}
                    label="Grad"
                    onChange={(e) => setCityId(e.target.value)}
                    disabled={!countryId}
                  >
                    {availableCities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Tip zemljišta</InputLabel>
                  <Select
                    value={landType}
                    label="Tip zemljišta"
                    onChange={(e) => setLandType(e.target.value)}
                  >
                    {Object.values(landTypeLabelMap).map((label) => (
                      <MenuItem key={label} value={label}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Opis
                </Typography>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Detaljan opis"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Detaljno opišite zemljište, njegove karakteristike, prednosti i potencijalne mogućnosti..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: C.white }}>
                  Lokacija na mapi
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: C.muted }}>
                  Opciono. Lokacija je vidljiva samo vama i investitorima.
                </Typography>
                <LocationPicker value={location} onChange={setLocation} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Slike
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    multiple
                    onChange={handleImageChange}
                  />
                  <label htmlFor="image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AddPhotoAlternateIcon />}
                      sx={{
                        borderColor: 'rgba(243,192,21,0.55)',
                        color: C.white,
                        '&:hover': {
                          background: C.goldGrad,
                          color: C.onGold,
                        },
                      }}
                    >
                      Dodaj slike
                    </Button>
                  </label>
                  <Typography variant="body2" sx={{ mt: 1, color: C.muted }}>
                    Možete dodati do 10 slika (maksimalna veličina: 5MB po slici)
                  </Typography>
                </Box>

                {(existingImages.length > 0 || previewUrls.length > 0) && (
                  <ImageList sx={{ width: '100%', height: 450 }} cols={3} rowHeight={164}>
                    {existingImages.map((image) => (
                      <ImageListItem key={image.id}>
                        <img
                          src={`/api/ads/uploads/thumbnails/${image.thumbnail_filename}`}
                          alt={`Existing ${image.order_index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          position="top"
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => removeExistingImage(image.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                    {previewUrls.map((url, index) => (
                      <ImageListItem key={`new-${index}`}>
                        <img
                          src={url}
                          alt={`New ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          position="top"
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => removeNewImage(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/my-ads')}
                    sx={{
                      borderColor: 'rgba(243,192,21,0.55)',
                      color: C.white,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: C.goldGrad,
                        color: C.onGold,
                      },
                    }}
                  >
                    Otkaži
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      background: C.goldGrad,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: C.goldGrad,
                      },
                      '&:disabled': {
                        backgroundColor: C.surface2,
                      },
                    }}
                  >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Sačuvaj izmene'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditAd; 